import {useMessages} from "i18n";
import {PlanCardModel} from "./pricingPlans";
import {Amount, Card, Check, Cta, Feature, Features, Flag, Name, Note, Per, PriceEyebrow, PriceRow, SavePill} from "./PlanCard.styles";

/**
 * One column of the pricing sheet (owner direction "Single Sheet", ui#2426). Dumb by design: it
 * renders a {@link PlanCardModel} and calls {@link Props.onCta}; no data-fetching, no plan logic (that
 * lives in {@link ./pricingPlans}). The recommended tier carries the sheet's one flag in flow above its
 * name; every other column renders a blank one so the names line up.
 */

export type PlanHeadingLevel = 2 | 3;

interface Props {
	plan: PlanCardModel;
	/** Matches the card title to its surface hierarchy: h2 on the page, h3 under the dialog h2. */
	headingLevel: PlanHeadingLevel;
	/** Fired when the card's CTA is pressed; only supplied for a purchasable tier. */
	onCta?: () => void;
}

export function PlanCard({plan, headingLevel, onCta}: Props) {
	const {welcome} = useMessages().pricing;
	const {variant} = plan;
	const headingTag = headingLevel === 2 ? "h2" : "h3";
	const flag = variant === "pop" ? plan.badge : undefined;
	return (
		<Card $variant={variant} aria-label={plan.ariaLabel}>
			<Flag $blank={flag === undefined} aria-hidden={flag === undefined ? true : undefined}>{flag ?? "·"}</Flag>
			<Name as={headingTag}>{plan.name}</Name>
			{plan.welcome && <PriceEyebrow>{welcome.firstMonth}</PriceEyebrow>}
			<PriceRow $underEyebrow={plan.welcome === true}>
				{plan.amount && <Amount>{plan.amount}</Amount>}
				{plan.amount && plan.per && <Per>{plan.per}</Per>}
				{plan.savePill && <SavePill>{plan.savePill}</SavePill>}
			</PriceRow>
			{plan.note && <Note>{plan.note}</Note>}
			{plan.cta && (
				<Cta type="button" $cta={plan.cta.variant} disabled={plan.cta.disabled} onClick={onCta}>
					{plan.cta.label}
				</Cta>
			)}
			<Features>
				{plan.features.map((feature) => (
					<Feature key={feature}>
						<Check aria-hidden="true">
							✓
						</Check>
						{feature}
					</Feature>
				))}
			</Features>
		</Card>
	);
}
